import { Chip, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Button } from "@mui/material";
// import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Link } from "react-router-dom";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import TableStyle from "./style";
import { Data, Order } from "./types";
import headCells from "./headCells";
import PaymentDetailsModal from "./PaymentDetailsModal";

import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import TableSkeleton from "@/components/app/TableSkeleton";
import TableLoading from "@/components/app/Loading/tableLoading";
import ROUTE_CONSTANTS from "@/constants/route-constants";

export default function TransactionTable({
    data: rows,
    totalItems,
    rowsPerPage,
    page,
    onChange,
    isLoading,
    isFetching,
    originalData, // Add this prop to get access to original transaction data
}: {
    data: Data[],
    isPending?: boolean,
    isLoading: boolean,
    isFetching: boolean,
    totalItems: number,
    page: number,
    rowsPerPage: number,
    originalData?: any[], // Original transaction data from API
    // eslint-disable-next-line no-unused-vars
    onChange: (data: { page?: number, rowsPerPage?: number }) => void,
}) {
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("paidAt");
    const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
    const [modalOpen, setModalOpen] = useState(false);
    // const [confirmModalId, setConfirmModal] = useState<number | null>(null);
    // const navigate = useNavigate();

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => onChange({ page: newPage, rowsPerPage });
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) =>
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

    const handleMoreClick = (transaction: any) => {
        setSelectedTransaction(transaction);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedTransaction(null);
    };

    return (
        <TableStyle>
            <TableContainer>
                <Table
                    stickyHeader
                    aria-labelledby="tableTitle"
                    className="table"
                    component="div"
                    size="medium"
                    sx={{ padding: ".5em" }}
                >
                    <EnhancedTableHead<Data>
                        hasAction={false}
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div" style={{ position: "relative" }}>
                        {isLoading && <TableSkeleton cells={headCells.map((item) => item.id)} />}
                        {!isLoading && !isFetching && rows.length === 0 && (
                            <TableRow>
                                <TableCell align="center" colSpan={headCells.length}>
                                    No Data To Show
                                </TableCell>
                            </TableRow>
                        )}
                        {isFetching && <TableLoading />}
                        {rows.map((row, index) => {
                            const {
                                id,
                                userId,
                                customerEmail,
                                status,
                                amount,
                                currency,
                                planPrice,
                                planName,
                                paidAt,
                                method,
                                paymentSource,
                            } = row;

                            const isSuccess = status.toUpperCase() === "SUCCESS" || status.toUpperCase() === "AUTHORIZED";
                            const isPending = status.toUpperCase() === "PENDING" || status.toUpperCase() === "INITIATED";

                            // Find the original transaction data for this row
                            const originalTransaction = originalData?.[index];

                            return (
                                <TableRow key={id}>
                                    <TableCell>
                                        <span className="table-value">{id}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">{paidAt}</span>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            color={isSuccess ? "success" : isPending ? "warning" : "error"}
                                            icon={isSuccess ? <GridCheckCircleIcon /> : <CancelIcon />}
                                            label={status}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">{planName}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">
                                            {amount ? `${amount.toFixed(2)} ${currency}` : "-"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">{planPrice}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">{method}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">{paymentSource}</span>
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            style={{ color: "#0096c7", textDecoration: "underline" }}
                                            to={
                                                ROUTE_CONSTANTS.USER_MANAGEMENT.CUSTOMERS.SHOW.SHOW_BY_ID(Number(userId))
                                                    .ABSOLUTE
                                            }
                                        >
                                            {userId}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <span className="table-value">{customerEmail}</span>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<MoreHorizIcon />}
                                            onClick={() => handleMoreClick(originalTransaction)}
                                            disabled={!originalTransaction}
                                        >
                                            More
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <PaymentDetailsModal open={modalOpen} onClose={handleCloseModal} transaction={selectedTransaction} />
        </TableStyle>
    );
}
